import Paper from "@mui/material/Paper"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TablePagination from "@mui/material/TablePagination"
import TableRow from "@mui/material/TableRow"
import { createUseStyles, useTheme } from "react-jss"
import { Box, Button, Typography } from "@mui/material"
import React, { useEffect, useState } from "react"
import Eye from "../../icons/eye.svg"
import { IMaintenanceRunColumn } from "../../util/interface"
import { Status } from "../../components/Status"
import { useAppDispatch, useAppSelector } from "../../util/hooks"
import {
  maintenanceLastModifiedDate,
  maintenanceViewPipeLineBaseDataAsync,
  openDefaultConfigDialog,
  openDialog,
  openScheduleDialog,
  resetDefaultScheduleRunDetails,
  resetUserChangedTabs,
  resetViewConfigData,
  setRunModalChartActive,
  updateViewConfigData,
} from "./MaintenanceSlice"
import Trend from "../../icons/trend.svg"

import Back from "../../icons/back.svg"
import Pipeline from "../../icons/pipelinedp.svg"
import UseCase from "../../icons/usecase-maintenance.svg"
import Market from "../../icons/market-maintenance.svg"

import ExecutionHistoryChart from "./ExecutionHistoryChart"
import {
  colorMapperGant,
  maintenanceViews,
  pipeLineStatusIcons,
  textMapperGant,
} from "../../util/StaticData"
import { ViewConfig } from "./ViewConfig"
import { pipelineConfigMapping } from "../../util/CommonMethods"
import CustomIcon from "../../components/CustomIcon"
import { NewPipeLineTabs } from "../../util/NewPipelineConfig"

const useStyles = createUseStyles({
  wrapper: {
    fontSize: 12,
  },
  root: {
    "& .MuiTableCell-head": {
      backgroundColor: "#fbfbfb",
    },
    height: 85,
  },
  heading: {
    fontSize: 16,
    fontWeight: 600,
  },
  subHeading: {
    color: "#7D7D7D",
    fontSize: 14,
    fontWeight: 600,
  },
  borderRight: {
    borderRight: "1px solid lightgray",
  },
  durationWrapper: {},
  duration: {
    fontSize: 12,
    fontWeight: 500,
    color: "#000000",
    // lineHeight: 15
    lineHeight: "14px",
  },
  durationPlaceHolder: {
    fontSize: 10,
    color: "#8E8E8E",
  },
  time: {
    color: "#7D7D7D",
    fontSize: 10,
    fontWeight: 500,
  },
  start: {},
  end: {},
  webSiteSubTitle: {
    fontSize: 10,
  },
  webSite: {
    fontSize: 12,
  },
  equalMargin: {
    marginLeft: "auto",
    marginRight: "auto",
  },
})

const convertToDate = (dateString: string) => {
  const [datePart, timePart] = dateString.split(", ")
  const [day, month, year] = datePart.split("/")
  const formattedDate = `${year}-${month}-${day} ${timePart}`

  return new Date(formattedDate)
}

const columns: IMaintenanceRunColumn[] = [
  {
    id: "pipelineRunIds",
    label: "Pipeline Run ID's",
    minWidth: 170,
    align: "left",
  },
  {
    id: "executionDate",
    label: "Execution Date",
    minWidth: 170,
    align: "left",
  },
  {
    id: "duration",
    label: "Duration",
    minWidth: 170,
    align: "center",
  },
  {
    id: "status",
    label: "Status",
    minWidth: 170,
    align: "left",
  },
]

export const RunInfo = ({ currentView }: { currentView: string }) => {
  const [page, setPage] = React.useState(0) // Current page state
  const [rowsPerPage, setRowsPerPage] = React.useState(10) // Rows per page
  const theme: any = useTheme() // Theme for styling
  const classes = useStyles({ theme }) // Custom styles
  const [cols, setCols] = useState(columns) // Column configuration
  const allRuns = useAppSelector(
    (store) => store.maintenance.executionHistoryData, // Selector for execution history data
  )
  const [rows, setRows] = useState(allRuns) // State for rows of the table
  useEffect(() => {
    setRows(allRuns) // Update rows when allRuns changes
  }, [allRuns])

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage) // Update page state
  }

  // Adjust columns based on the current view
  useEffect(() => {
    if (currentView === maintenanceViews.sideview) {
      const tempCols = cols.filter((obj) => obj.id !== "duration")
      setCols(tempCols) // Remove duration column
    } else if (currentView === maintenanceViews.dialogView) {
      dispatch(maintenanceLastModifiedDate()) // Fetch last modified date
    }
  }, [currentView])

  const lastModifiedDate = useAppSelector(
    (store) => store.maintenance.lastModifiedDate, // Selector for last modified date
  )

  const dispatch = useAppDispatch() // Hook to get the dispatch function
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(+event.target.value) // Update rows per page
    setPage(0) // Reset page to 0
  }

  const openScheduleDialogLocal = () => {
    dispatch(resetDefaultScheduleRunDetails()) // Reset schedule run details
    dispatch(openScheduleDialog()) // Open schedule dialog
  }

  const openDefaultConfigDialogLocal = () => {
    dispatch(openDefaultConfigDialog()) // Open default config dialog
    dispatch(resetUserChangedTabs()) // Reset user changed tabs
  }

  const isChartActive = useAppSelector(
    (state) => state.maintenance.isRunModalChartActive,
  )
  const toggleChart = () => {
    dispatch(setRunModalChartActive()) // Toggle chart state
  }

  const activeRunDetails = useAppSelector(
    (store) => store.maintenance.activeRunDetails, // Selector for active run details
  )

  // Information about the current run
  const runsInfo = [
    {
      svg: UseCase,
      label: "Usecase",
      value: activeRunDetails.usecase,
    },
    {
      svg: Market,
      label: "Market",
      value: activeRunDetails.market,
    },
    {
      svg: Pipeline,
      label: "Pipeline Name",
      value: activeRunDetails.pipelineName,
    },
  ]

  const openViewConfig = (o: any) => {
    dispatch(resetViewConfigData()) // Reset view config data
    NewPipeLineTabs.forEach(async (obj) => {
      const response = await dispatch(
        maintenanceViewPipeLineBaseDataAsync({
          tab: obj.key,
          runId: o.pipelineRunIds,
        }),
      )
      let tableData = JSON.parse(response["payload"]["data_csv"]) // Parse CSV data
      tableData = pipelineConfigMapping(tableData, obj.key) // Map pipeline config data
      dispatch(updateViewConfigData({ key: obj.key, data: tableData })) // Update view config data
    })
    dispatch(openDialog()) // Open dialog for view config
  }

  // Render component layout
  const sortedRows = [...rows]?.sort((a: any, b: any) => {
    const dateA: any = convertToDate(a.executionDate)
    const dateB: any = convertToDate(b.executionDate)
    return dateB - dateA
  })

  return (
    <Box
      flexDirection={"column"}
      gap={currentView === maintenanceViews.sideview ? 2.4 : 2}
      display={"flex"}
    >
      <ViewConfig />
      <Box
        display={"flex"}
        flexDirection={
          currentView === maintenanceViews.sideview ? "column" : "row"
        }
        gap={currentView === maintenanceViews.sideview ? 1 : 3}
      >
        {runsInfo.map((obj) => (
          <Box
            key={obj.label}
            gap={currentView === maintenanceViews.sideview ? 1 : 2}
            display={"flex"}
          >
            <Box gap={0.5} display={"flex"}>
              <img src={obj.svg} alt="" />
              <Typography component={"div"} variant="label">
                {obj.label}:
              </Typography>
            </Box>
            <Typography component={"div"} variant="value">
              {obj.value}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box gap={1} display={"flex"}>
        <Button
          onClick={openScheduleDialogLocal}
          variant="contained"
          sx={{ color: "textColors.themeWhite", textTransform: "none" }}
        >
          Default Pipeline Run
        </Button>
        <Button
          onClick={openDefaultConfigDialogLocal}
          variant="contained"
          sx={{ color: "textColors.themeWhite", textTransform: "none" }}
        >
          Set Default Config
        </Button>
        {currentView === maintenanceViews.dialogView && (
          <Typography
            sx={{ display: "flex", alignItems: "center" }}
            variant="caption"
          >
            Last Updated: {lastModifiedDate}
          </Typography>
        )}
      </Box>

      <div>
        <Paper sx={{ width: "100%" }}>
          <Box sx={{ display: "flex", flexDirection: "row" }}>
            <Typography variant="tableHeaderMaintenance">
              Execution History
            </Typography>
            <Box
              onClick={toggleChart}
              className="border-gray-200 border ml-1 flex"
              sx={{ cursor: "pointer" }}
            >
              <CustomIcon path={isChartActive ? Back : Trend} alt="" />
            </Box>
          </Box>

          {!isChartActive ? (
            <>
              <TableContainer sx={{ maxHeight: 515, borderRadius: 10 }}>
                <Table stickyHeader aria-label="sticky table">
                  <TableHead className={classes.root}>
                    <TableRow>
                      {cols.map((column) => (
                        <TableCell
                          key={column.id}
                          align={column.align}
                          className={classes.wrapper}
                        >
                          <Typography variant="tableHeaderMaintenance">
                            {column.label}
                          </Typography>
                        </TableCell>
                      ))}
                      <TableCell
                        key={"Configs"}
                        align={"center"}
                        className={classes.wrapper}
                      >
                        <Typography variant="tableHeaderMaintenance">
                          Configs
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedRows
                      .slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage,
                      )
                      .map((row, index) => (
                        <TableRow
                          hover
                          role="checkbox"
                          tabIndex={-1}
                          key={index}
                        >
                          {cols.map((column) => {
                            const value = row[column.id]

                            if (column.id === "duration") {
                              return (
                                <TableCell
                                  className={classes.wrapper}
                                  key={column.id}
                                  align={column.align}
                                >
                                  <div className={classes.durationWrapper}>
                                    <div className={classes.duration}>
                                      {value}
                                    </div>
                                  </div>
                                </TableCell>
                              )
                            }

                            if (column.id === "status") {
                              return (
                                <TableCell
                                  className={classes.wrapper}
                                  key={column.id}
                                  align={column.align}
                                >
                                  <Status
                                    backgroundColor={colorMapperGant[value]}
                                    text={textMapperGant[value]}
                                    icon={pipeLineStatusIcons[value]}
                                    align={column.align}
                                  />
                                </TableCell>
                              )
                            }

                            return (
                              <TableCell
                                className={classes.wrapper}
                                key={column.id}
                                align={column.align}
                              >
                                <Box sx={{ fontSize: 12 }}>{value}</Box>
                              </TableCell>
                            )
                          })}
                          <TableCell
                            className={classes.wrapper}
                            key={"configs"}
                            align={"center"}
                            onClick={() => openViewConfig(row)} // Click to open view config
                            sx={{ cursor: "pointer" }}
                          >
                            <img
                              className={classes.equalMargin}
                              src={Eye}
                              alt=""
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                rowsPerPageOptions={[10, 20]}
                component="div"
                count={rows.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage} // Handle page change
                onRowsPerPageChange={handleChangeRowsPerPage} // Handle rows per page change
              />
            </>
          ) : (
            <ExecutionHistoryChart /> // Show execution history chart
          )}
        </Paper>
      </div>
    </Box>
  )
}
